<template>
  <div class="home-page">
    <!-- Hero Banner -->
    <section class="hero-banner">
      <div class="hero-content">
        <h1>{{ t.hero.title }}</h1>
        <p>{{ t.hero.subtitle }}</p>
        <a href="#products" class="btn-primary">{{ t.hero.browseProducts }}</a>
      </div>
    </section>

    <!-- 产品展示区域 -->
    <section id="products" class="products-section">
      <div class="container">
        <h2 class="section-title">{{ t.products.title }}</h2>
        <div class="products-grid" v-if="products.length > 0">
          <div 
            v-for="product in products" 
            :key="product.id"
            class="product-card"
          >
            <div class="product-image" @click="goToProductDetail(product.id)">
              <img :src="product.image" :alt="product.name" v-if="product.image" @error="handleImageError">
              <div class="product-placeholder" v-else>{{ t.products.noImage }}</div>
              <div class="product-overlay" @click.stop="goToProductDetail(product.id)">
                <span class="view-detail">{{ t.products.viewDetail }}</span>
              </div>
            </div>
            <div class="product-info" @click="goToProductDetail(product.id)">
              <span class="product-type">{{ product.type }}</span>
            </div>
          </div>
        </div>
        <div v-else class="loading-message">
          {{ t.products.loading }}
        </div>
      </div>
    </section>

    <!-- 公司优势 -->
    <section id="features" class="features-section">
      <div class="container">
        <h2 class="section-title">{{ t.features.title }}</h2>
        <div class="features-grid">
          <div class="feature-item">
            <div class="feature-icon">
              <span>{{ t.features.experience.number }}</span>
            </div>
            <h3>{{ t.features.experience.title }}</h3>
            <p>{{ t.features.experience.desc }}</p>
          </div>
          <div class="feature-item">
            <div class="feature-icon">
              <span>{{ t.features.coverage.number }}</span>
            </div>
            <h3>{{ t.features.coverage.title }}</h3>
            <p>{{ t.features.coverage.desc }}</p>
          </div>
          <div class="feature-item">
            <div class="feature-icon">
              <span>{{ t.features.support.number }}</span>
            </div>
            <h3>{{ t.features.support.title }}</h3>
            <p>{{ t.features.support.desc }}</p>
          </div>
          <div class="feature-item">
            <div class="feature-icon">
              <span>{{ t.features.quality.number }}</span>
            </div>
            <h3>{{ t.features.quality.title }}</h3>
            <p>{{ t.features.quality.desc }}</p>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { messages } from '../i18n/index.js'

// 产品ID列表 - 根据实际文件夹确定
const MACHINE_IDS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21]

export default {
  name: 'HomePage',
  props: {
    locale: {
      type: String,
      default: 'en'
    }
  },
  data() {
    return {
      products: [],
      isLoading: false
    }
  },
  computed: {
    t() {
      return messages[this.locale] || messages.en
    }
  },
  created() {
    this.loadProducts()
  },
  methods: {
    // 处理图片路径
    processImagePath(imgPath) {
      if (!imgPath) return ''
      if (imgPath.startsWith('/assets/')) {
        try {
          return require(`../assets${imgPath.replace('/assets', '')}`)
        } catch (e) {
          console.warn('图片加载失败:', imgPath)
          return imgPath
        }
      }
      return imgPath
    },

    async loadProducts() {
      this.isLoading = true
      try {
        const machines = []
        
        // 动态加载所有产品数据
        for (const id of MACHINE_IDS) {
          try {
            const module = await import(`../assets/machine/${id}/machine_txt.js`)
            const data = module.machine_txt || module.default?.machine_txt
            if (data) {
              machines.push({ id, data })
            }
          } catch (e) {
            console.warn(`产品 ${id} 数据加载失败:`, e)
          }
        }

        // 处理产品数据
        this.products = machines.map(machine => {
          const data = machine.data || {}
          
          // 获取第一张图片作为主图
          let mainImage = ''
          if (data.machine_img && Array.isArray(data.machine_img) && data.machine_img.length > 0) {
            mainImage = this.processImagePath(data.machine_img[0])
          }
          
          // 生成简短描述
          let shortDesc = ''
          if (data.description && Array.isArray(data.description) && data.description.length > 0) {
            const firstDesc = data.description[0]
            shortDesc = firstDesc.length > 80 ? firstDesc.substring(0, 80) + '...' : firstDesc
          }

          return {
            id: machine.id,
            type: data.type || 'Unknown Model',
            name: data.name || 'Unnamed Product',
            image: mainImage,
            shortDesc: shortDesc || 'No description available',
            fullData: data
          }
        })
      } catch (error) {
        console.error('加载产品数据失败:', error)
        this.products = []
      } finally {
        this.isLoading = false
      }
    },
    handleImageError(e) {
      // 图片加载失败时显示占位符
      console.warn('图片加载失败:', e.target.src)
      e.target.style.display = 'none'
      e.target.nextElementSibling.style.display = 'flex'
    },
    goToProductDetail(productId) {
      // 跳转到产品详情页
      console.log('点击产品，ID:', productId)
      if (productId) {
        this.$emit('select-product', productId)
      } else {
        console.error('产品ID无效:', productId)
      }
    }
  }
}
</script>

<style scoped>
.home-page {
  min-height: 100vh;
}

/* Hero Banner */
.hero-banner {
  background: linear-gradient(135deg, #16181c 0%, #25282e 50%, #16181c 100%);
  padding: var(--space-4xl) 0 var(--space-3xl);
  text-align: center;
  color: var(--color-surface);
  margin-top: 40px;
  position: relative;
  overflow: hidden;
}

.hero-banner::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(ellipse at 30% 30%, rgba(196, 18, 48, 0.12) 0%, transparent 55%),
              radial-gradient(ellipse at 70% 70%, rgba(230, 57, 80, 0.08) 0%, transparent 50%);
  pointer-events: none;
  animation: heroPulse 12s ease-in-out infinite alternate;
}

@keyframes heroPulse {
  0% { transform: scale(1); }
  100% { transform: scale(1.08); }
}

.hero-content {
  position: relative;
  z-index: 1;
  max-width: 820px;
  margin: 0 auto;
  padding: var(--space-xl) var(--space-lg);
}

.hero-content h1 {
  font-size: var(--font-size-4xl);
  font-weight: var(--font-weight-bold);
  margin-bottom: var(--space-lg);
  background: linear-gradient(90deg, #fff 0%, #fff 55%, var(--color-primary-light) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: var(--line-height-tight);
  letter-spacing: var(--letter-spacing-tight);
}

.hero-content p {
  font-size: var(--font-size-lg);
  color: rgba(255, 255, 255, 0.82);
  margin-bottom: var(--space-2xl);
  max-width: 620px;
  margin-left: auto;
  margin-right: auto;
  line-height: var(--line-height-normal);
  letter-spacing: var(--letter-spacing-wide);
}

/* 产品展示区域 */
.products-section {
  padding: var(--space-4xl) 0;
  background: linear-gradient(180deg, var(--color-background) 0%, var(--color-surface) 100%);
}

.products-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: var(--space-xl);
}

.product-card {
  background: var(--color-surface);
  border-radius: var(--radius-xl);
  overflow: hidden;
  box-shadow: var(--shadow-md);
  cursor: pointer;
  transition: all var(--transition-slow);
  border: 1px solid var(--color-border);
  position: relative;
}

.product-card::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: var(--radius-xl);
  border: 1px solid transparent;
  transition: border-color var(--transition-base);
  pointer-events: none;
}

.product-card:hover {
  transform: translateY(-8px);
  box-shadow: var(--shadow-xl);
}

.product-card:hover::after {
  border-color: var(--color-border-hover);
}

.product-image {
  position: relative;
  height: 240px;
  background: linear-gradient(135deg, #f4f5f7 0%, #fafbfc 100%);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.product-image img {
  max-width: 92%;
  max-height: 92%;
  width: auto;
  height: auto;
  object-fit: contain;
  transition: transform var(--transition-slow);
}

.product-placeholder {
  width: 100%;
  height: 100%;
  display: none;
  align-items: center;
  justify-content: center;
  color: var(--color-text-subtle);
  font-size: var(--font-size-sm);
  background: var(--color-background);
}

.product-card:hover .product-image img {
  transform: scale(1.06);
}

.product-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(196, 18, 48, 0.88);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity var(--transition-base);
  backdrop-filter: blur(2px);
}

.product-card:hover .product-overlay {
  opacity: 1;
}

.view-detail {
  color: var(--color-surface);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  padding: var(--space-md) var(--space-xl);
  border: 2px solid var(--color-surface);
  border-radius: var(--radius-md);
  transition: all var(--transition-base);
  letter-spacing: var(--letter-spacing-wide);
}

.view-detail:hover {
  background: var(--color-surface);
  color: var(--color-primary);
}

.product-info {
  padding: var(--space-lg);
  display: flex;
  align-items: center;
  min-height: 72px;
}

.product-type {
  display: inline-block;
  background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-light) 100%);
  color: var(--color-surface);
  padding: var(--space-xs) var(--space-md);
  border-radius: var(--radius-sm);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  letter-spacing: var(--letter-spacing-wide);
}

.loading-message {
  text-align: center;
  padding: var(--space-3xl);
  color: var(--color-text-muted);
  font-size: var(--font-size-md);
}

/* 公司优势 */
.features-section {
  padding: var(--space-4xl) 0;
  background: linear-gradient(180deg, var(--color-surface) 0%, var(--color-background) 100%);
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: var(--space-lg);
}

.feature-item {
  text-align: center;
  padding: var(--space-2xl) var(--space-xl);
  background: var(--color-surface);
  border-radius: var(--radius-xl);
  transition: all var(--transition-slow);
  border: 1px solid var(--color-border);
  box-shadow: var(--shadow-sm);
}

.feature-item:hover {
  transform: translateY(-8px);
  box-shadow: var(--shadow-lg);
  border-color: var(--color-border-hover);
}

.feature-icon {
  width: 88px;
  height: 88px;
  margin: 0 auto var(--space-lg);
  background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-light) 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: var(--shadow-primary);
  transition: transform var(--transition-bounce);
}

.feature-item:hover .feature-icon {
  transform: scale(1.08);
}

.feature-icon span {
  color: var(--color-surface);
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  letter-spacing: var(--letter-spacing-tight);
}

.feature-item h3 {
  font-size: var(--font-size-lg);
  color: var(--color-text-heading);
  margin-bottom: var(--space-sm);
  font-weight: var(--font-weight-semibold);
  letter-spacing: var(--letter-spacing-tight);
}

.feature-item p {
  font-size: var(--font-size-base);
  color: var(--color-text-muted);
  line-height: var(--line-height-normal);
}

/* 响应式 - 平板 */
@media (max-width: 1024px) {
  .features-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: var(--space-lg);
  }

  .products-grid {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: var(--space-lg);
  }
}

/* 响应式 - 手机 */
@media (max-width: 768px) {
  .hero-banner {
    padding: var(--space-3xl) var(--space-md) var(--space-2xl);
    margin-top: 0;
  }

  .hero-content h1 {
    line-height: var(--line-height-tight);
  }

  .hero-content p {
    margin-bottom: var(--space-xl);
  }

  .products-section {
    padding: var(--space-3xl) 0;
  }

  .products-grid {
    grid-template-columns: 1fr;
    gap: var(--space-lg);
  }

  .product-image {
    height: 220px;
  }

  .product-info {
    padding: var(--space-md);
    min-height: 64px;
  }

  .features-section {
    padding: var(--space-3xl) 0;
  }

  .features-grid {
    grid-template-columns: 1fr;
    gap: var(--space-md);
  }

  .feature-item {
    padding: var(--space-lg) var(--space-md);
    display: flex;
    align-items: center;
    text-align: left;
    gap: var(--space-md);
  }

  .feature-icon {
    width: 68px;
    height: 68px;
    margin: 0;
    flex-shrink: 0;
  }

  .feature-icon span {
    font-size: var(--font-size-lg);
  }

  .feature-item h3 {
    font-size: var(--font-size-md);
  }

  .feature-item p {
    font-size: var(--font-size-sm);
  }
}

/* 响应式 - 小屏手机 */
@media (max-width: 480px) {
  .hero-banner {
    padding: var(--space-2xl) var(--space-md) var(--space-xl);
  }

  .product-image {
    height: 200px;
  }

  .feature-item {
    flex-direction: column;
    text-align: center;
    padding: var(--space-lg) var(--space-md);
  }

  .feature-icon {
    width: 60px;
    height: 60px;
    margin: 0 auto var(--space-md);
  }
}
</style>
